
import { Component, Vue, Watch, Prop, Emit } from "vue-property-decorator"
import { namespace, Getter, Action, Mutation } from "vuex-class"

// @ts-ignore
import VuePerfectScrollbar from "vue-perfect-scrollbar"
import { BModal } from "bootstrap-vue"
import moment from "moment"
import { get, set } from "lodash"
import { calculatedValueDisplay } from "@/mixins/calculatedValueDisplay"

import vSelect from "vue-select"
import FieldPermissions from "./Permissions/FieldPermissions.vue"
import DataSourceSelect from "@/components/Fields/DataSourceSelect.vue"
import DataSourceTemplate from "@/components/Fields/DataSourceTemplate.vue"
import Numeric from "../Fields/Numeric.vue"
import DateTime from "../Fields/DateTime.vue"
import MapLocation from "../Fields/MapLocation.vue"
import ExternalLink from "../Fields/ExternalLink.vue"

import FieldControl from "../Fields/FieldControl.vue"

import { CalculationType } from "@/types"

import { maxInputLength } from "@/constants/input"

import api from "@/services/api"
import ConfigurationColumn from "@/types/configurationColumn"

@Component({
	name: "DataForm",
	components: {
		"v-select": vSelect,
		"numeric": Numeric,
		"field-permissions": FieldPermissions,
		"data-select": DataSourceSelect,
		"data-template": DataSourceTemplate,
		"date-time": DateTime,
		"map-location": MapLocation,
		"details-modal": () => import("@/components/CaseDetails/DataDetailsModal.vue"),
		"external-link": ExternalLink,
		"field": FieldControl
	}
})
export default class DataForm extends Vue {
	$refs!: {
		viewItemDetails: any
	}

	@Prop()
	columns!: ConfigurationColumn[];

	@Prop()
	canEdit!: boolean;

	@Prop({ type: Boolean, default: false })
	setItemLabels!: boolean;

	@Prop()
	public deleteDetails!: (item: any) => any;

	@Prop()
	item: any;

	public get columnErrors () {
		return this.columns.filter(c => c.errors)
			.reduce((group: any, column: any) => {
				const error = column.errors(this.item)
				group[column.key] = error

				if (column.errorStatus && column.errorStatus[this.item] && !group[column.key]) {
					group[column.key] = "Error"
				}
				if (column.errorValue === this.item) {
					group[column.key] = "Checking ... "
				}

				return group
			}, {})
	}

	public async mounted () {
		if (this.isValid) {
			this.$emit("valid")
		} else {
			this.$emit("invalid")
		}
	}

	public get isValid () {
		return this.columns.every((c: any) => !this.columnErrors[c.key])
	}

	public get columnSections () {
		if (!this.columns) {
			return []
		}

		return this.columns.filter((column) => column.inModal === undefined || column.inModal(this.item))
			.map((c: any) => c.section ? c.section : "")
			.filter((c: any, index: number, array: any[]) => array.indexOf(c) === index)
	}

	public get modalColumnsBySection () {
		if (!this.item) {
			return []
		}

		return this.columns.filter((column) => column.inModal === undefined || column.inModal(this.item))
			.reduce((group: any, column: any) => {
				var section = column.section ? column.section : "";
				(group[section] = group[section] || []).push(column)
				return group
			}, {})
	}

	public itemValue (item: any, columnKey: string): any {
		if (!item) {
			return null
		}
		var value = get(item, columnKey)

		// If the items a calulated field
		if (item.dataType === "calculated" && value && typeof value === "object") {
			// Add the field's id to the value object
			value.id = item ? item.id : null
		}

		return value
	}

	public setItemValue (item: any, columnKey: string, value: any, linkedColumn: any = null) {
		if (linkedColumn) {
			var index = this.columns.findIndex(c => c.key === linkedColumn.columnKey)
			if (index > -1) {
				var column = this.columns[index]
				column.componentProperties[linkedColumn.prop] = value
				this.columns[index] = column
			}
		}
		const dotIndex = columnKey.indexOf(".")
		if (dotIndex > -1) {
			const topLevel = columnKey.substring(0, dotIndex)
			const rest = columnKey.substring(dotIndex + 1)

			if (item[topLevel] == null) {
				this.$emit("change")
				Vue.set(item, topLevel, {})
			}

			this.setItemValue(item[topLevel], rest, value)
		} else {
			this.$emit("change")
			Vue.set(item, columnKey, value)
		}
	}

	public setItemLabel (item: any, columnKey: string, value: any) {
		if (!this.setItemLabels) return

		set(item, columnKey + "-label", value.label)
	}

	public setLabeledValueLabel (item: any, column: any, value: any) {
		if (!column.labelKeyPath) {
			return
		}
		set(item, column.labelKeyPath, value.label)
	}

	private changeColumnCheck (columnKey: string) {
		this.item[columnKey] = !this.item[columnKey]
	}

	@Watch("isValid")
	public validChanged (valid: boolean) {
		if (valid) {
			this.$emit("valid")
		} else {
			this.$emit("invalid")
		}
	}

	private async addItem (item: any, columnKey: string, newValue: any): Promise<void> {
		let value = get(item, columnKey)
		if (!value) {
			value = []

			// Init value on item
			const addTo = item
			const currentPath = columnKey.split(".")
			currentPath.forEach((path, index) => {
				if (index === currentPath.length - 1) {
					Vue.set(item, path, value)
				} else {
					let pathItem = get(item, path)
					if (!pathItem) {
						pathItem = {}
						Vue.set(item, path, pathItem)
					}
					item = pathItem
				}
			})
		}

		value.push(newValue)
	}

	private async removeItem (item: any, columnKey: string, newValue: any): Promise<void> {
		const value = get(item, columnKey)
		if (value) {
			value.removeWhere((item: any) => item.id === newValue.id)
		}
	}

	private async updateItem (item: any, columnKey: string, newValue: any): Promise<void> {
		const value = get(item, columnKey)
		if (value) {
			const updateItem = value.find((item: any) => item.id === newValue.id)
			if (updateItem) {
				updateItem.label = newValue.label
				Object.keys(newValue.fields).forEach(fieldId => {
					updateItem.fields[fieldId] = newValue.fields[fieldId]
				})

				this.$emit("change")
			}
		}
	}

	private openNewItemModal ({ source, definition, addNewItem, addItem, data, updateItem }:
		{ source: string, definition: string, addNewItem: boolean, addItem: () => void, data: any, updateItem: any }) {
		this.$refs.viewItemDetails.open({ source, definition, addNewItem, addItem, data, updateItem })
	}

	private getSelectOptions (options: any, item:any) {
		if (typeof (options) === "function") {
			return options(item)
		}
		return options
	}

	private formatLength (input: string): string {
		return String(input).substring(0, maxInputLength)
	}

	private isFieldReadOnly (item: any, column: ConfigurationColumn): boolean {
		if (!column) return false

		if (typeof (column.readonly) === "boolean") {
			return column.readonly
		} else if (typeof (column.readonly) === "function") {
			return column.readonly(item)
		}
		return !!column.readonly
	}
}

